/**
 * @fileoverview Centralized subdomain configuration and detection utilities
 * @module config/subdomain
 *
 * Handles multi-environment subdomain patterns:
 * - Production: acme.planthra.com
 * - Staging: acme.staging.planthra.com
 * - Local: acme.localhost
 *
 * @see docs/standards.md - Multi-Tenancy Architecture
 */

/**
 * Reserved subdomains that cannot be used by tenants.
 * Aligned with server-side validation in tenant/create.ts
 */
export const RESERVED_SUBDOMAINS = [
  'www',
  'app',
  'api',
  'admin',
  'dashboard',
  'help',
  'support',
  'docs',
  'status',
  'blog',
  'mail',
  'ftp',
  'cdn',
  'assets',
  'static',
  'staging',
  'dev',
  'test',
  'demo',
  'planthra',
  'auth',
  'login',
  'signup'
] as const

export type ReservedSubdomain = (typeof RESERVED_SUBDOMAINS)[number]

/**
 * Base domains by environment
 */
export const BASE_DOMAINS = {
  production: 'planthra.com',
  staging: 'staging.planthra.com',
  localhost: 'localhost'
} as const

export type Environment = keyof typeof BASE_DOMAINS

/**
 * Check if a subdomain is reserved
 *
 * @param subdomain - The subdomain to check
 * @returns True if the subdomain is reserved
 */
export function isReservedSubdomain(subdomain: string): boolean {
  return RESERVED_SUBDOMAINS.includes(
    subdomain.toLowerCase() as ReservedSubdomain
  )
}

/**
 * Get the current environment based on hostname
 *
 * @returns The detected environment
 */
export function detectEnvironment(): Environment {
  const hostname = window.location.hostname
  if (hostname.includes('localhost')) return 'localhost'
  if (hostname.includes('staging.planthra.com')) return 'staging'
  return 'production'
}

/**
 * Detect tenant subdomain from URL hostname.
 * Handles both production and staging patterns.
 *
 * @example
 * // Production: acme.planthra.com -> 'acme'
 * // Staging: acme.staging.planthra.com -> 'acme'
 * // Reserved: www.planthra.com -> null
 * // No subdomain: planthra.com -> null
 * // Localhost: acme.localhost -> 'acme'
 *
 * @returns Tenant subdomain or null if none/reserved
 */
export function detectSubdomain(): string | null {
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  // Handle localhost (e.g., acme.localhost)
  if (hostname.includes('localhost')) {
    if (parts.length > 1) {
      const sub = parts[0].toLowerCase()
      return isReservedSubdomain(sub) ? null : sub
    }
    return null
  }

  // Staging pattern: {subdomain}.staging.planthra.com (4 parts)
  // e.g., ['acme', 'staging', 'planthra', 'com']
  if (parts.length === 4 && parts[1] === 'staging') {
    const sub = parts[0].toLowerCase()
    return isReservedSubdomain(sub) ? null : sub
  }

  // Production pattern: {subdomain}.planthra.com (3 parts)
  // e.g., ['acme', 'planthra', 'com']
  if (parts.length === 3) {
    const sub = parts[0].toLowerCase()
    return isReservedSubdomain(sub) ? null : sub
  }

  // Base domain (2 parts) or unknown pattern
  return null
}

/**
 * Build the full subdomain URL for a given subdomain
 *
 * @param subdomain - The tenant subdomain
 * @param environment - The target environment (defaults to current)
 * @returns The full URL with subdomain
 */
export function buildSubdomainUrl(
  subdomain: string,
  environment?: Environment
): string {
  const env = environment ?? detectEnvironment()
  const baseDomain = BASE_DOMAINS[env]
  const protocol = env === 'localhost' ? 'http' : 'https'
  const port = env === 'localhost' ? ':5173' : ''

  return `${protocol}://${subdomain}.${baseDomain}${port}`
}
